import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  User, Phone, Mail, Calendar, ArrowLeft, Utensils,
  FileText, Activity, FileBarChart, Receipt, Edit, X, Save,
  Send, MessageSquare, Clock, CheckCircle, Copy, Trash2, Stethoscope, Plus, Link, Apple,
  MapPin, MonitorSmartphone
} from 'lucide-react'
import api from '../../services/api'
import { format, differenceInYears, differenceInDays } from 'date-fns'
import { fr } from 'date-fns/locale'
import toast from 'react-hot-toast'

// composants des onglets
import PatientNotesTab from './components/PatientNotesTab'
import PatientMeasurementsTab from './components/PatientMeasurementsTab'
import PatientInvoicesTab from './components/PatientInvoicesTab'
import PatientDocumentsTab from './components/PatientDocumentsTab'
import PatientDietPlan from '../../components/admin/PatientDietPlan'
import PatientMealLogsTab from './components/PatientMealLogsTab'

export default function PatientDetailsPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const[patient, setPatient] = useState(null)
  const [loading, setLoading] = useState(true)
  const[activeTab, setActiveTab] = useState('summary')
  const [searchParams] = useSearchParams()
  const [sendFormAutoOpen, setSendFormAutoOpen] = useState(false)
  const[reasons, setReasons] = useState([]) 

  // États pour l'édition patient
  const[isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editData, setEditData] = useState({})

  // État tâche rapide
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)
  const [taskData, setTaskData] = useState({ content: '', due_date: '' })

  // État modale de relance formulaire
  const [relanceModal, setRelanceModal] = useState(null)
  const [sendVia, setSendVia] = useState({ email: true, sms: false })
  const [isSending, setIsSending] = useState(false)

  // État Réseau de soins
  const [isNetworkModalOpen, setIsNetworkModalOpen] = useState(false)
  const[globalNetwork, setGlobalNetwork] = useState([])
  const [selectedMemberId, setSelectedMemberId] = useState('')

  // État modale Portail Patient
  const [isPortalModalOpen, setIsPortalModalOpen] = useState(false)
  const [portalLink, setPortalLink] = useState('')


  useEffect(() => {
    const action = searchParams.get('action')
    if (action === 'new_note') setActiveTab('notes')
    if (action === 'send_form') { setActiveTab('notes'); setSendFormAutoOpen(true) }
  }, [searchParams])

  useEffect(() => {
    fetchPatient()
    api.get('/api/admin/reasons').then(res => setReasons(res.data)).catch(console.error)
  }, [id])

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
    } else {
      const ta = document.createElement("textarea")
      ta.value = text; document.body.appendChild(ta); ta.select()
      document.execCommand("copy"); ta.remove()
    }
    toast.success("Lien copié !")
  }

  const fetchPatient = async () => {
    try {
      const res = await api.get(`/api/patients/${id}`)
      // Appel réseau (on peuple une propriété simulée ou on attend le backend)
      // Normalement, `res.data.care_network` arrivera tout seul grâce au back !
      setPatient(res.data)
      setEditData(res.data)
    } catch (error) { toast.error("Erreur chargement patient") } 
    finally { setLoading(false) }
  }

  // --- LOGIQUE RÉSEAU DE SOINS ---
  const handleOpenNetworkModal = async () => {
      try {
          const res = await api.get('/api/care-network/directory')
          setGlobalNetwork(res.data)
          setIsNetworkModalOpen(true)
      } catch(e) { toast.error("Erreur chargement annuaire") }
  }

  const handleLinkMember = async () => {
      if(!selectedMemberId) return
      try {
          await api.post(`/api/care-network/patient/${id}/link/${selectedMemberId}`)
          toast.success("Confrère ajouté à l'équipe du patient")
          setSelectedMemberId('')
          fetchPatient()
          setIsNetworkModalOpen(false)
      } catch(e) { toast.error("Erreur d'ajout") }
  }

  const handleUnlinkMember = async (memberId) => {
      if(!confirm("Retirer ce confrère de l'équipe du patient ?")) return
      try {
          await api.delete(`/api/care-network/patient/${id}/link/${memberId}`)
          toast.success("Retiré")
          fetchPatient()
      } catch(e) { toast.error("Erreur") }
  }
  // -------------------------------

  const handleUpdatePatient = async () => {
    try {
      await api.patch(`/api/patients/${id}`, {
        first_name: editData.first_name, last_name: editData.last_name, gender: editData.gender,
        maiden_name: editData.maiden_name, email: editData.email, phone: editData.phone,
        address: editData.address, birth_date: editData.birth_date, ss_number: editData.ss_number,
        main_motive: editData.main_motive, pathology: editData.pathology,
        prescribing_doctor: editData.prescribing_doctor, notes: editData.notes
      })
      setIsEditModalOpen(false)
      toast.success("Fiche patient mise à jour")
      fetchPatient()
    } catch (error) { toast.error("Erreur lors de la mise à jour") }
  }

  const handleRelance = async () => {
    if (!sendVia.email && !sendVia.sms) return toast.error("Sélectionnez au moins un canal.")
    setIsSending(true)
    const toastId = toast.loading("Envoi en cours...")
    let errors =[]
    try {
      if (sendVia.email) { try { await api.post(`/api/forms/requests/${relanceModal.req_id}/send-email`) } catch { errors.push("Email") } }
      if (sendVia.sms) { try { await api.post(`/api/forms/requests/${relanceModal.req_id}/send-sms`) } catch { errors.push("SMS") } }
      if (errors.length === 0) {
        toast.success(sendVia.email && sendVia.sms ? "Relance (Email + SMS) !" : sendVia.email ? "Relance (Email) !" : "Relance (SMS) !", { id: toastId })
        setRelanceModal(null)
      } else { toast.error(`Échec : ${errors.join(" et ")}`, { id: toastId }) }
    } finally { setIsSending(false) }
  }

  const handleCancelFormRequest = async (reqId) => {
    if (!confirm("Supprimer cette demande de formulaire ?")) return
    try { await api.delete(`/api/forms/requests/${reqId}`); toast.success("Demande supprimée."); fetchPatient() } 
    catch (e) { toast.error(e.response?.data?.detail || "Erreur.") }
  }

  const handleCopyFormLink = (token) => {
    const url = `${window.location.origin}/form/${token}`
    if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(url)
    else { const ta = document.createElement("textarea"); ta.value = url; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove() }
    toast.success("Lien copié !")
  }

  if (loading) return <div className="flex justify-center p-12"><div className="spinner" /></div>
  if (!patient) return <div className="p-12 text-center">Patient introuvable</div>

  const age = patient.birth_date ? differenceInYears(new Date(), new Date(patient.birth_date)) : 'N/A'
  const nextAppointment = patient.appointments?.filter(apt => new Date(apt.date) >= new Date().setHours(0, 0, 0, 0) && apt.status !== 'cancelled').sort((a, b) => new Date(a.date) - new Date(b.date))[0]
  const pendingForms = patient.form_requests?.filter(fr => fr.status === 'pending') ??[]

  const TABS =[
    { id: 'summary',      label: 'Résumé',          icon: User        },
    { id: 'notes',        label: 'Notes & Suivi',    icon: FileText    },
    { id: 'measurements', label: 'Mensurations',     icon: Activity    },
    { id: 'diet',         label: 'Plan Alimentaire', icon: Utensils    },
    { id: 'carnet',       label: 'Portail (Patient)', icon: MonitorSmartphone },
    { id: 'documents',    label: 'Documents',        icon: FileBarChart },
    { id: 'invoices',     label: 'Factures',         icon: Receipt     },
  ]

  let avatarColor = "bg-sage-100 text-sage-600"
  let honorific = ""
  let naissance = ""
  if (patient.gender === 'F') { avatarColor = "bg-pink-100 text-pink-600"; honorific = "Mme"; naissance = "Née le" } 
  else if (patient.gender === 'M') { avatarColor = "bg-blue-100 text-blue-600"; honorific = "M." ; naissance = "Né le" }

  return (
    <div className="space-y-6 h-full flex flex-col">
      <div className="flex flex-col lg:flex-row gap-6 items-start">
            
            {/* 1. RETOUR ET AVATAR */}
            <div className="flex items-start gap-4 shrink-0">
                <button onClick={() => navigate('/admin/patients')} className="p-2 hover:bg-gray-100 rounded-full text-gray-400 mt-1"><ArrowLeft className="w-5 h-5" /></button>
                <div className={`w-20 h-20 rounded-full flex items-center justify-center font-serif text-3xl font-bold shadow-inner ${avatarColor}`}>
                    {patient.first_name[0]}{patient.last_name[0]}
                </div>
            </div>

            {/* 2. IDENTITÉ, CONTACTS & MÉDICAL */}
            <div className="flex-1 min-w-0 flex flex-col md:flex-row gap-6 justify-between">
                
                {/* Colonne Gauche : Identité & Médical */}
                <div className="flex-1 min-w-0">
                    <h1 className="text-2xl font-serif text-gray-800 font-bold flex flex-wrap items-center gap-2">
                        {honorific} {patient.first_name} {patient.last_name}
                        {patient.maiden_name && <span className="text-lg font-normal text-gray-400 italic">née {patient.maiden_name}</span>}
                        <button onClick={() => { setEditData(patient); setIsEditModalOpen(true) }} className="text-gray-400 hover:text-sage-600 transition-colors p-1"><Edit className="w-4 h-4" /></button>
                    </h1>
                    
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                        <span className="bg-gray-100 px-2 py-0.5 rounded text-xs font-bold text-gray-700">{age} ans</span>
                        {naissance}{patient.birth_date && <span>{format(new Date(patient.birth_date), 'dd/MM/yyyy')}</span>}
                        {patient.ss_number && <span className="text-xs text-gray-400 font-mono ml-2 border-l border-gray-200 pl-2">SS: {patient.ss_number}</span>}
                    </div>

                    {patient.main_motive && (
                        <div className="mt-4 mb-1"><span className="text-sm font-bold text-sage-700 bg-sage-50 border border-sage-100 px-2 py-1 rounded-md">🎯 {patient.main_motive}</span></div>
                    )}
                    
                    {patient.pathology && (
                        <div className="mt-3 bg-red-50 border border-red-100 rounded-lg p-3 text-sm text-red-800 max-h-32 overflow-y-auto scrollbar-thin">
                            <span className="font-bold flex items-center gap-1.5 mb-1"><Activity className="w-4 h-4"/> Pathologies / Contexte :</span>
                            <p className="whitespace-pre-wrap leading-relaxed">{patient.pathology}</p>
                        </div>
                    )}
                </div>

                {/* Colonne Droite : Contacts (Aligné en face de l'identité) */}
                <div className="flex flex-col gap-3 text-sm text-gray-600 shrink-0 md:pt-2 md:w-56">
                    <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-sage-50 flex items-center justify-center shrink-0">
                            <Phone className="w-3.5 h-3.5 text-sage-600" />
                        </div>
                        <span className="font-bold text-gray-800">{patient.phone}</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-sage-50 flex items-center justify-center shrink-0">
                            <Mail className="w-3.5 h-3.5 text-sage-600" />
                        </div>
                        <a href={`mailto:${patient.email}`} className="truncate hover:text-sage-600 hover:underline" title={patient.email}>
                            {patient.email || "Pas d'email"}
                        </a>
                    </div>
                    <div className="flex items-start gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-sage-50 flex items-center justify-center shrink-0 mt-0.5">
                            <MapPin className="w-3.5 h-3.5 text-sage-600" />
                        </div>
                        <span className="leading-tight pt-1">{patient.address || "Adresse non renseignée"}</span>
                    </div>
                </div>
                
            </div>

            {/* 3. RESEAU DE SOINS (Le bloc contact ayant déménagé) */}
            <div className="w-full lg:w-72 flex flex-col gap-4 shrink-0">
                {/* Bloc Équipe Médicale */}
                <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                    <div className="flex justify-between items-center mb-2">
                        <h4 className="text-xs font-bold text-blue-800 uppercase tracking-wide flex items-center gap-1.5"><Stethoscope className="w-3.5 h-3.5"/> Équipe Médicale</h4>
                        <button onClick={handleOpenNetworkModal} className="text-blue-600 hover:bg-blue-100 p-1 rounded-full transition-colors" title="Lier un confrère"><Plus className="w-4 h-4"/></button>
                    </div>
                    {patient.prescribing_doctor && (
                        <div className="text-xs text-gray-600 mb-2 border-b border-blue-100 pb-2">
                            Prescripteur: <span className="font-bold text-gray-800">{patient.prescribing_doctor}</span>
                        </div>
                    )}
                    <div className="space-y-2">
                        {patient.care_network?.length > 0 ? patient.care_network.map(member => (
                            <div key={member.id} className="flex justify-between items-start bg-white p-2 rounded-lg border border-blue-100 shadow-sm group">
                                <div className="min-w-0">
                                    <p className="text-xs font-bold text-gray-800 truncate">{member.first_name} {member.last_name}</p>
                                    <p className="text-[10px] text-blue-600 font-medium">{member.specialty}</p>
                                    {member.phone && <p className="text-[10px] text-gray-500 mt-0.5">{member.phone}</p>}
                                </div>
                                <button onClick={() => handleUnlinkMember(member.id)} className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3 h-3"/></button>
                            </div>
                        )) : (
                            <p className="text-xs text-gray-400 italic">Aucun confrère lié au dossier.</p>
                        )}
                    </div>
                </div>
            </div>

            {/* 4. ACTIONS (RDV, Clochette, etc.) */}
            <div className="flex flex-col gap-2 w-full lg:w-48 shrink-0">
                {/* NOUVEAU BOUTON : PORTAIL PATIENT */}
                <button 
                    onClick={async () => {
                        try {
                            const res = await api.post(`/api/portal/generate-link/${patient.id}`)
                            setPortalLink(res.data.url)
                            setSendVia({ email: !!patient.email, sms: false })
                            setIsPortalModalOpen(true)
                        } catch(e) { toast.error("Erreur de génération") }
                    }}
                    className="w-full btn bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 py-2 text-sm flex items-center justify-center gap-2 shadow-sm transition-colors"
                >
                    📱 App Patient
                </button>


                <div className="flex gap-2">
                    <button onClick={() => navigate('/admin/appointments', { state: { targetPatient: patient } })} className="btn btn-primary flex-1 py-2 text-sm flex items-center justify-center gap-2"><Calendar className="w-4 h-4" /> RDV</button>
                    <button onClick={() => setIsTaskModalOpen(true)} className="btn bg-yellow-100 text-yellow-700 hover:bg-yellow-200 px-3 py-2 text-sm flex items-center justify-center transition-colors" title="Rappel">🔔</button>
                </div>
                <div className="text-xs text-center text-gray-400 mt-1 cursor-pointer hover:text-sage-600" onClick={() => nextAppointment && navigate(`/admin/appointments?action=open&apt_id=${nextAppointment.id}&return_to=/admin/patients/${patient.id}`)}>
                    Prochain RDV : <span className="font-bold text-gray-600">{nextAppointment ? `${format(new Date(nextAppointment.date), 'dd/MM')} à ${nextAppointment.start_time.slice(0,5)}` : 'Aucun'}</span>
                </div>
                {pendingForms.length > 0 && (
                    <div onClick={() => setActiveTab('summary')} className="text-xs text-center bg-orange-50 border border-orange-200 text-orange-700 rounded-lg px-2 py-1.5 cursor-pointer hover:bg-orange-100 transition-colors flex items-center justify-center gap-1 mt-1">
                        <Clock className="w-3 h-3" /> {pendingForms.length} formulaire(s) en attente
                    </div>
                )}
            </div>
      </div>

      <div className="flex gap-6 mt-8 border-b border-gray-100 overflow-x-auto scrollbar-hide">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`pb-3 px-1 flex items-center gap-2 text-sm font-medium transition-all relative whitespace-nowrap ${activeTab === tab.id ? 'text-sage-700' : 'text-gray-400 hover:text-gray-600'}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
            {tab.id === 'summary' && pendingForms.length > 0 && <span className="bg-orange-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{pendingForms.length}</span>}
            {activeTab === tab.id && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-sage-500 rounded-t-full" />}
          </button>
        ))}
      </div>

      <div className="flex-1 bg-white rounded-2xl shadow-sm border border-sage-100 p-6 overflow-hidden">
        {activeTab === 'summary' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* COL 1 : PROCHAINS RDV */}
            <div>
              <h3 className="text-lg font-serif text-sage-700 mb-4 flex items-center gap-2"><Calendar className="w-5 h-5" /> À venir</h3>
              <div className="space-y-3">
                {patient.appointments.filter(apt => new Date(apt.date) >= new Date().setHours(0, 0, 0, 0) && apt.status !== 'cancelled').sort((a, b) => new Date(a.date) - new Date(b.date)).map(apt => (
                    <div key={apt.id} onClick={() => navigate(`/admin/appointments?action=open&apt_id=${apt.id}&return_to=/admin/patients/${patient.id}`)} className="bg-white border border-sage-200 rounded-xl p-4 shadow-sm hover:shadow-md hover:border-sage-400 cursor-pointer transition-all flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className="bg-sage-100 text-sage-600 font-bold px-3 py-1 rounded-lg text-center min-w-[60px]">
                          <div className="text-xs uppercase">{format(new Date(apt.date), 'MMM', { locale: fr })}</div><div className="text-lg">{format(new Date(apt.date), 'dd')}</div>
                        </div>
                        <div><p className="font-bold text-gray-700">{apt.start_time.slice(0, 5)}</p><p className="text-xs text-gray-500 capitalize">{apt.consultation_type}</p></div>
                      </div>
                      <div className={`px-2 py-1 rounded text-xs font-bold ${apt.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{apt.status === 'confirmed' ? 'Confirmé' : 'En attente'}</div>
                    </div>
                ))}
                {patient.appointments.filter(apt => new Date(apt.date) >= new Date().setHours(0, 0, 0, 0) && apt.status !== 'cancelled').length === 0 && (
                  <div className="text-gray-400 italic text-sm p-4 border border-dashed rounded-xl text-center">Aucun rendez-vous planifié.<br /><button onClick={() => navigate('/admin/appointments', { state: { targetPatient: patient } })} className="text-sage-600 font-bold hover:underline mt-1">Prendre RDV maintenant</button></div>
                )}
              </div>
            </div>
            {/* COL 2 : HISTORIQUE */}
            <div>
              <h3 className="text-lg font-serif text-gray-500 mb-4 flex items-center gap-2"><Activity className="w-5 h-5" /> Historique</h3>
              <div className="space-y-3 relative">
                <div className="absolute left-[29px] top-2 bottom-2 w-0.5 bg-gray-100 -z-10" />
                {patient.appointments.filter(apt => new Date(apt.date) < new Date().setHours(0, 0, 0, 0) && apt.status !== 'cancelled').sort((a, b) => new Date(b.date) - new Date(a.date)).map(apt => (
                    <div key={apt.id} className="flex items-center gap-4 group">
                      <div className="w-14 text-center text-gray-400 text-xs font-medium bg-gray-50 rounded p-1 border border-gray-100">{format(new Date(apt.date), 'dd/MM')}<br />{format(new Date(apt.date), 'yyyy')}</div>
                      <div onClick={() => navigate(`/admin/appointments?action=open&apt_id=${apt.id}&return_to=/admin/patients/${patient.id}`)} className="flex-1 bg-gray-50 border border-gray-100 rounded-lg p-3 hover:bg-white hover:shadow-sm hover:border-sage-200 cursor-pointer transition-all">
                        <div className="flex justify-between">
                          <span className="font-bold text-gray-600 text-sm">{apt.consultation_type}</span>
                          {apt.payment_status === 'paid' ? <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full border border-green-200 font-medium">Payé</span> : apt.payment_status === 'pending' ? <span className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full border border-orange-200 font-medium">En attente</span> : <span className="text-[10px] bg-red-50 text-red-600 px-2 py-0.5 rounded-full border border-red-100 font-medium">Non réglé</span>}
                        </div>
                      </div>
                    </div>
                ))}
                {patient.appointments.filter(apt => new Date(apt.date) < new Date().setHours(0, 0, 0, 0)).length === 0 && <p className="text-gray-400 italic text-sm">Aucun historique.</p>}
              </div>
            </div>
            {/* COL 3 : EN ATTENTE */}
            <div>
              <h3 className="text-lg font-serif text-orange-600 mb-4 flex items-center gap-2"><Clock className="w-5 h-5" /> En attente</h3>
              {pendingForms.length === 0 ? (
                <div className="text-gray-400 italic text-sm p-4 border border-dashed border-gray-100 rounded-xl text-center"><CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-300" />Aucun formulaire en attente.</div>
              ) : (
                <div className="space-y-3">
                  {pendingForms.map(fr => {
                    const daysSince = differenceInDays(new Date(), new Date(fr.created_at))
                    return (
                      <div key={fr.id} className="bg-orange-50 border border-orange-200 rounded-xl p-4 shadow-sm">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1 min-w-0"><p className="font-bold text-gray-800 text-sm truncate">{fr.template_name}</p><p className="text-xs text-gray-400 mt-0.5">Envoyé il y a {daysSince === 0 ? "aujourd'hui" : `${daysSince} j`} {' · '}{format(new Date(fr.created_at), 'dd/MM/yyyy')}</p></div>
                          <span className="ml-2 text-[10px] bg-orange-100 text-orange-700 border border-orange-200 px-2 py-0.5 rounded-full font-bold whitespace-nowrap">En attente</span>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => handleCopyFormLink(fr.token)} className="flex items-center justify-center gap-1.5 text-xs font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-lg py-1.5 px-2 transition-colors"><Copy className="w-3 h-3" /> Copier</button>
                          <button onClick={() => { setRelanceModal({ req_id: fr.id, form_name: fr.template_name, token: fr.token }); setSendVia({ email: !!patient.email, sms: false }) }} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium bg-orange-500 hover:bg-orange-600 text-white rounded-lg py-1.5 transition-colors"><Send className="w-3 h-3" /> Relancer</button>
                          <button onClick={() => handleCancelFormRequest(fr.id)} className="flex items-center justify-center gap-1.5 text-xs font-medium bg-white border border-red-200 text-red-500 hover:bg-red-50 rounded-lg py-1.5 px-2 transition-colors"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
              {/* FORMULAIRES RECUS */}
              {patient.form_requests?.filter(fr => fr.status === 'completed').length > 0 && (
                  <div className="mt-8 pt-6 border-t border-gray-100">
                      <h3 className="text-lg font-bold text-green-700 mb-4 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" /> Réponse(s) au questionnaire
                      </h3>
                      <div className="space-y-3">
                          {patient.form_requests.filter(fr => fr.status === 'completed').map(fr => (
                              <div key={fr.id} className="bg-green-50 border border-green-200 rounded-xl p-4 shadow-sm">
                                  <div className="flex-1 min-w-0 mb-3">
                                      <p className="font-bold text-gray-800 text-sm truncate">{fr.template_name}</p>
                                      <p className="text-xs text-gray-500 mt-0.5">
                                          Rempli le {format(new Date(fr.created_at), 'dd/MM/yyyy')}
                                      </p>
                                  </div>
                                  <button 
                                      onClick={() => setActiveTab('notes')} 
                                      className="w-full text-xs font-bold text-green-700 bg-white border border-green-200 px-3 py-2 rounded-lg hover:bg-green-100 transition-colors flex justify-center items-center gap-2"
                                  >
                                      <FileText className="w-4 h-4"/> Lire dans les Notes & Suivi
                                  </button>
                              </div>
                          ))}
                      </div>
                  </div>
              )}
            </div>
          </div>
        )}
        {activeTab === 'notes' && <PatientNotesTab patientId={patient.id} patient={patient} autoOpenSendForm={sendFormAutoOpen} />}
        {activeTab === 'measurements' && <PatientMeasurementsTab patientId={patient.id} patientGender={patient.gender} patientAge={age} />}
        {activeTab === 'diet' && <PatientDietPlan patientId={patient.id} />}
        {activeTab === 'carnet' && <PatientMealLogsTab patient={patient} onChange={fetchPatient} />}
        {activeTab === 'documents' && <PatientDocumentsTab patient={patient} />}
        {activeTab === 'invoices' && <PatientInvoicesTab patientId={patient.id} />}
      </div>


      {/* --- MODALE ÉDITION PATIENT --- */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-gray-100 pb-4">
              <h3 className="text-lg font-serif font-bold text-gray-800">Modifier le patient</h3>
              <button onClick={() => setIsEditModalOpen(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                  <label className="text-xs font-bold text-gray-500 uppercase">Nom de jeune fille</label>
                  <input value={editData.maiden_name || ''} onChange={e => setEditData({ ...editData, maiden_name: e.target.value })} className="w-full mt-1" placeholder="Optionnel" />
                </div>
              <div>
                  <label className="text-xs font-bold text-gray-500 uppercase">Motif global prise en charge</label>
                  <select
                      value={editData.main_motive || ''} 
                      onChange={e => setEditData({ ...editData, main_motive: e.target.value })} 
                      className="w-full mt-1 bg-sage-50 border-sage-200 focus:bg-white font-medium text-sm py-2.5"
                  >
                      <option value="">Non défini</option>
                      {reasons.map(r => (
                          <option key={r.id} value={r.name}>{r.name}</option>
                      ))}
                  </select>
              </div>
            </div>
              <div>
                <label className="text-xs font-bold text-red-500 uppercase">Pathologies / Contexte</label>
                <textarea 
                    value={editData.pathology || ''} 
                    onChange={e => setEditData({ ...editData, pathology: e.target.value })} 
                    className="w-full mt-1 bg-red-50 border-red-200 focus:bg-white text-sm" 
                    placeholder="Ex: Stéatose hépatique, Obésité grade I..."
                    rows={2}
                />
              </div>
              <div>
                <label className="text-xs font-bold text-blue-600 uppercase">Médecin prescripteur</label>
                <input 
                    value={editData.prescribing_doctor || ''} 
                    onChange={e => setEditData({ ...editData, prescribing_doctor: e.target.value })} 
                    className="w-full mt-1 bg-blue-50 border-blue-200 focus:bg-white text-sm" 
                    placeholder="Ex: Dr. Dupont"
                />
              </div>
            <div className="grid grid-cols-10 gap-4">
              <div className="col-span-2">
                <label className="text-xs font-bold text-gray-500 uppercase">Sexe</label>
                <select value={editData.gender || ''} onChange={e => setEditData({ ...editData, gender: e.target.value })} className="w-full mt-1 text-sm py-2.5">
                  <option value="">-</option>
                  <option value="F">Femme</option>
                  <option value="M">Homme</option>
                </select>
              </div>
              <div className="col-span-4">
                <label className="text-xs font-bold text-gray-500 uppercase">Prénom</label>
                <input value={editData.first_name} onChange={e => setEditData({ ...editData, first_name: e.target.value })} className="w-full mt-1" />
              </div>
              <div className="col-span-4">
                <label className="text-xs font-bold text-gray-500 uppercase">Nom</label>
                <input value={editData.last_name} onChange={e => setEditData({ ...editData, last_name: e.target.value })} className="w-full mt-1" />
              </div>

            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Email</label>
                <input value={editData.email || ''} onChange={e => setEditData({ ...editData, email: e.target.value })} className="w-full mt-1" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Téléphone</label>
                <input value={editData.phone} onChange={e => setEditData({ ...editData, phone: e.target.value })} className="w-full mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Date de naissance</label>
                <input type="date" value={editData.birth_date || ''} onChange={e => setEditData({ ...editData, birth_date: e.target.value })} className="w-full mt-1" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">N° Sécu</label>
                <input value={editData.ss_number || ''} onChange={e => setEditData({ ...editData, ss_number: e.target.value })} className="w-full mt-1" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase">Adresse</label>
              <textarea value={editData.address || ''} onChange={e => setEditData({ ...editData, address: e.target.value })} rows={2} className="w-full mt-1" />
            </div>
            <button onClick={handleUpdatePatient} className="w-full btn btn-primary flex justify-center items-center gap-2">
              <Save className="w-4 h-4" /> Enregistrer
            </button>
          </div>
        </div>
      )}

      {/* --- MODALE NOUVELLE TÂCHE --- */}
      {isTaskModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-serif font-bold text-gray-800 flex items-center gap-2">
                🔔 Nouveau Rappel
              </h3>
              <button onClick={() => setIsTaskModalOpen(false)}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">À faire</label>
                <input
                  type="text" autoFocus
                  placeholder="Ex: Demander son bilan sanguin"
                  value={taskData.content}
                  onChange={e => setTaskData({ ...taskData, content: e.target.value })}
                  className="w-full mt-1 bg-yellow-50 focus:bg-white border-yellow-200 focus:border-yellow-400"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Date (Optionnelle)</label>
                <input
                  type="date"
                  value={taskData.due_date}
                  onChange={e => setTaskData({ ...taskData, due_date: e.target.value })}
                  className="w-full mt-1"
                />
              </div>
              <button
                disabled={!taskData.content.trim()}
                onClick={async () => {
                  try {
                    await api.post('/api/tasks/', {
                      content: taskData.content,
                      due_date: taskData.due_date || null,
                      patient_id: patient.id
                    })
                    toast.success("Rappel ajouté au tableau de bord !")
                    setIsTaskModalOpen(false)
                    setTaskData({ content: '', due_date: '' })
                  } catch (e) {
                    toast.error("Erreur lors de l'ajout")
                  }
                }}
                className="w-full btn bg-yellow-400 hover:bg-yellow-500 text-yellow-900 flex justify-center items-center gap-2 mt-2"
              >
                <Save className="w-4 h-4" /> Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- MODALE RELANCE FORMULAIRE --- */}
      {relanceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-serif font-bold text-gray-800 flex items-center gap-2">
                <Send className="w-5 h-5 text-orange-500" /> Relancer le patient
              </h3>
              <button onClick={() => setRelanceModal(null)}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
            </div>

            {/* Rappel du formulaire */}
            <div className="bg-orange-50 border border-orange-100 rounded-xl p-3 mb-5">
              <p className="text-xs text-orange-600 font-bold uppercase mb-0.5">Formulaire</p>
              <p className="text-sm font-bold text-gray-800">{relanceModal.form_name}</p>
              <p className="text-xs text-gray-500 mt-1">
                Patient : {patient.first_name} {patient.last_name}
              </p>
            </div>

            {/* Canal d'envoi */}
            <div className="mb-5">
              <label className="block text-xs font-bold text-gray-500 uppercase mb-3">Envoyer via</label>
              <div className="grid grid-cols-2 gap-3">
                {/* Email */}
                <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer select-none transition-all ${
                  sendVia.email ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-gray-50'
                } ${!patient.email ? 'opacity-40 cursor-not-allowed' : ''}`}>
                  <input type="checkbox" className="hidden"
                    checked={sendVia.email} disabled={!patient.email}
                    onChange={e => setSendVia(p => ({ ...p, email: e.target.checked }))}
                  />
                  <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                    sendVia.email ? 'bg-orange-500' : 'bg-white border-2 border-gray-300'
                  }`}>
                    {sendVia.email && <CheckCircle className="w-4 h-4 text-white" />}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-700 flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email</p>
                    <p className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[90px]">{patient.email || 'Non renseigné'}</p>
                  </div>
                </label>
                {/* SMS */}
                <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer select-none transition-all ${
                  sendVia.sms ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'
                } ${!patient.phone ? 'opacity-40 cursor-not-allowed' : ''}`}>
                  <input type="checkbox" className="hidden"
                    checked={sendVia.sms} disabled={!patient.phone}
                    onChange={e => setSendVia(p => ({ ...p, sms: e.target.checked }))}
                  />
                  <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                    sendVia.sms ? 'bg-blue-500' : 'bg-white border-2 border-gray-300'
                  }`}>
                    {sendVia.sms && <CheckCircle className="w-4 h-4 text-white" />}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-700 flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> SMS</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{patient.phone || 'Non renseigné'}</p>
                  </div>
                </label>
              </div>
            </div>

            <button
              onClick={handleRelance}
              disabled={isSending || (!sendVia.email && !sendVia.sms)}
              className="w-full btn bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center gap-2 py-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSending
                ? <><div className="spinner w-4 h-4 border-white" /> Envoi...</>
                : <><Send className="w-4 h-4" /> Envoyer la relance</>
              }
            </button>
          </div>
        </div>
      )}

      {/* --- NOUVELLE MODALE : ASSOCIER AU RÉSEAU DE SOINS --- */}
      {isNetworkModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6">
                <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-3">
                    <h3 className="text-lg font-serif font-bold text-blue-900 flex items-center gap-2"><Link className="w-5 h-5 text-blue-500" /> Ajouter à l'équipe</h3>
                    <button onClick={() => setIsNetworkModalOpen(false)}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">Sélectionner un confrère de l'annuaire</label>
                        <select 
                            value={selectedMemberId} 
                            onChange={e => setSelectedMemberId(e.target.value)} 
                            className="w-full mt-1 bg-blue-50 border-blue-200"
                        >
                            <option value="">Sélectionnez...</option>
                            {globalNetwork.map(m => (
                                <option key={m.id} value={m.id}>{m.first_name} {m.last_name} ({m.specialty})</option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-400 mt-2 italic">Vous pouvez enrichir votre annuaire global depuis l'onglet "Paramètres / Réseaux & Partenaires".</p>
                    </div>
                    <button 
                        onClick={handleLinkMember}
                        disabled={!selectedMemberId}
                        className="w-full btn bg-blue-600 hover:bg-blue-700 text-white flex justify-center items-center gap-2 mt-2 disabled:opacity-50"
                    >
                        Ajouter au dossier
                    </button>
                </div>
            </div>
          </div>
      )}

      {/* --- TRANSMETTRE PORTAIL PATIENT --- */}
      {isPortalModalOpen && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6">
                <div className="flex justify-between items-center mb-5">
                    <h3 className="text-lg font-serif font-bold text-indigo-900 flex items-center gap-2">
                        📱 Espace Patient
                    </h3>
                    <button onClick={() => setIsPortalModalOpen(false)}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
                </div>

                <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 mb-5">
                    <p className="text-xs text-indigo-600 font-bold uppercase mb-1">Lien généré (Valable 30j)</p>
                    <div className="flex items-center gap-2">
                        <input type="text" readOnly value={portalLink} className="w-full text-[10px] text-gray-500 bg-white border-none py-2 rounded" />
                        <button onClick={() => copyToClipboard(portalLink)} className="p-2 bg-indigo-100 text-indigo-600 rounded hover:bg-indigo-200" title="Copier">
                            <Copy className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="mb-5">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-3">Transmettre au patient via :</label>
                    <div className="grid grid-cols-2 gap-3">
                        <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer select-none transition-all ${sendVia.email ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-gray-50'} ${!patient.email ? 'opacity-40 cursor-not-allowed' : ''}`}>
                            <input type="checkbox" className="hidden" checked={sendVia.email} disabled={!patient.email} onChange={e => setSendVia(p => ({ ...p, email: e.target.checked }))} />
                            <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${sendVia.email ? 'bg-indigo-500' : 'bg-white border-2 border-gray-300'}`}>
                                {sendVia.email && <CheckCircle className="w-4 h-4 text-white" />}
                            </div>
                            <div>
                                <p className="text-sm font-bold text-gray-700 flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email</p>
                            </div>
                        </label>
                        <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer select-none transition-all ${sendVia.sms ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-gray-50'} ${!patient.phone ? 'opacity-40 cursor-not-allowed' : ''}`}>
                            <input type="checkbox" className="hidden" checked={sendVia.sms} disabled={!patient.phone} onChange={e => setSendVia(p => ({ ...p, sms: e.target.checked }))} />
                            <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${sendVia.sms ? 'bg-indigo-500' : 'bg-white border-2 border-gray-300'}`}>
                                {sendVia.sms && <CheckCircle className="w-4 h-4 text-white" />}
                            </div>
                            <div>
                                <p className="text-sm font-bold text-gray-700 flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> SMS</p>
                            </div>
                        </label>
                    </div>
                </div>

                <button
                    onClick={async () => {
                        if (!sendVia.email && !sendVia.sms) return toast.error("Sélectionnez un canal.")
                        setIsSending(true)
                        try {
                            // On mappe les variables Frontend vers les clés attendues par le Backend
                            const payload = { via_email: sendVia.email, via_sms: sendVia.sms }
                            await api.post(`/api/portal/send-link/${patient.id}`, payload)
                            toast.success("Lien transmis avec succès !")
                            setIsPortalModalOpen(false)
                        } catch (e) { toast.error("Erreur d'envoi") }
                        finally { setIsSending(false) }
                    }}
                    disabled={isSending || (!sendVia.email && !sendVia.sms)}
                    className="w-full btn bg-indigo-600 hover:bg-indigo-700 text-white flex items-center justify-center gap-2 py-3 disabled:opacity-50"
                >
                    {isSending ? <div className="spinner w-4 h-4 border-white" /> : <><Send className="w-4 h-4" /> Envoyer l'accès</>}
                </button>
            </div>
          </div>
      )}

    </div>
  )
}

// importé dans lucide react, à garder pour référence si besoin de personnalisation
//function MapPin(props) {
//  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
//}